import { useRef, useState, type DragEvent, type ChangeEvent } from "react";
import { Alert, Box, Button, IconButton, LinearProgress, Typography, alpha } from "@mui/material";
import { CloudUploadOutlined, Close as CloseIcon, InsertDriveFileOutlined } from "@mui/icons-material";
import { useNotify } from "react-admin";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { uploadDocument, validateFileType } from "@/providers/uploadService";
import { DocumentRecordType, DocumentUploadStatus, DropzoneDocumentType } from "@/types";
import { FILE_CONSTANTS } from "@/constants/documentUpload";
import { formatFileSize } from "@/utils/formatters/fileUtils";

// =============================================================================
// ClaimantDocumentUpload — storage-only reference-document uploader (#6506)
// =============================================================================
// A claimant sibling of PolicyPdfUpload. Two deliberate differences:
//   1. NO carrier/vendor gate. PolicyPdfUpload hard-blocks when a policy has no
//      vendor; claimants have no vendor_id_ref, and the upload service only sends
//      X-Vendor-Id-Ref when present — so the header is simply omitted.
//   2. A BROADER allow-list (DropzoneDocumentType.ATTACHMENTS) — "other
//      documents", not the policy .pdf/.xls/.xlsx set (#6506 AC).
//
// Rejections (type / size) are rendered as a PERSISTENT inline error, not a
// transient toast (#6463 / #6506 AC). Upload always uses the storage-only path
// (X-Storage-Only: true) so the AI pipeline is skipped — no extraction, no load,
// no review task. Filenames are never logged or put in notification bodies (PHI,
// ADR-086); the success toast is filename-free.

const ACCEPTED_EXTENSIONS = FILE_CONSTANTS.DROPZONE_DOCUMENT.ATTACHMENTS.ACCEPTED_EXTENSIONS;

interface ClaimantDocumentUploadProps {
  /** Pre-populate client context (claimant.client_id_ref). Optional. */
  clientIdRef?: string;
  /**
   * Called with the new document id after the upload is accepted. May be async
   * (it performs the attach); a rejection means the document was saved but not
   * linked, and the caller reports failure rather than success.
   */
  onUploadComplete: (newDocumentId: string) => void | Promise<void>;
  /** When true, the drop zone expands to fill its parent container. */
  fullHeight?: boolean;
}

interface FileEntry {
  file: File;
  status: "pending" | "uploading" | "success" | "error";
  progress: number;
  error?: string;
  // The document id returned by a successful upload. Retained so a retry after an
  // attach failure re-attaches THIS document instead of re-uploading — which
  // would mint a second storage-only PHI blob attached to nothing (#6528).
  uploadedDocId?: string;
}

export const ClaimantDocumentUpload = ({
  clientIdRef,
  onUploadComplete,
  fullHeight = false,
}: ClaimantDocumentUploadProps) => {
  const { theme, isDarkMode } = useAppTheme();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fileEntry, setFileEntry] = useState<FileEntry | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Persistent (not a toast) rejection message for a bad type / oversized file
  // (#6463): a transient toast is easy to miss and the AC calls for a message
  // that stays until the user acts. Cleared on the next valid selection.
  const [validationError, setValidationError] = useState<string | null>(null);

  // Returns whether the file was accepted, so callers (e.g. a multi-file drop)
  // can decide what to say without racing the async validationError state.
  const handleFileSelected = (file: File): boolean => {
    const validation = validateFileType(file, DropzoneDocumentType.ATTACHMENTS);
    if (!validation.valid) {
      // Persistent inline error, not notify() — do NOT include the filename (PHI).
      setValidationError(validation.error ?? "This file can’t be attached.");
      setFileEntry(null);
      return false;
    }
    setValidationError(null);
    setFileEntry({ file, status: "pending", progress: 0 });
    return true;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelected(file);
    e.target.value = ""; // reset so the same file can be re-selected
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (!files.length) return;
    // Single-file uploader: don't silently swallow a multi-file drop — take the
    // first (if valid) and say so, rather than discarding the rest with no
    // feedback. Set the note AFTER selection so it isn't cleared by an accept.
    const accepted = handleFileSelected(files[0]);
    if (accepted && files.length > 1) {
      setValidationError("One file at a time — attaching just the first. Drop the others separately.");
    }
  };

  const handleUpload = async () => {
    if (!fileEntry || fileEntry.status === "uploading") return;

    setFileEntry((prev) => (prev ? { ...prev, status: "uploading", progress: 0 } : null));

    try {
      // Retry-safe: if a prior attempt already uploaded (attach then failed),
      // reuse that document id and only re-attach — never re-upload, which would
      // orphan a storage-only PHI blob (#6528).
      let documentId = fileEntry.uploadedDocId;
      if (!documentId) {
        const result = await uploadDocument(
          {
            id: crypto.randomUUID(),
            file: fileEntry.file,
            name: fileEntry.file.name,
            // Storage-only, non-processing attachment: stamp the non-processing
            // record type (#6505 / ADR-086) so it isn't mislabelled, and skip the
            // AI pipeline entirely. The claimant relation is written separately via
            // the attachments endpoint (onUploadComplete → panel handleAttach).
            record_type: DocumentRecordType.ATTACHMENT,
            // No vendor_id_ref — claimants have none; the service omits the header.
            client_id_ref: clientIdRef,
            storage_only: true,
            mime_type: fileEntry.file.type || "application/octet-stream",
            status: DocumentUploadStatus.PENDING,
            progress: 0,
            error: null,
          },
          (progress) => {
            setFileEntry((prev) => (prev ? { ...prev, progress } : null));
          }
        );
        documentId = result.id as string;
        // Remember the uploaded id BEFORE the attach, so an attach failure leaves
        // it recorded for the retry path above.
        setFileEntry((prev) => (prev ? { ...prev, uploadedDocId: documentId, progress: 100 } : null));
      }

      // Report success only once BOTH upload AND attach land — awaiting the
      // attach means its failure throws into the catch below and shows one
      // accurate message instead of a success toast contradicted by a warning.
      await onUploadComplete(documentId);

      setFileEntry((prev) => (prev ? { ...prev, status: "success", progress: 100 } : null));
      notify("Document uploaded successfully", { type: "success" });
      setTimeout(() => setFileEntry(null), 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      setFileEntry((prev) => (prev ? { ...prev, status: "error", error: message } : null));
      notifyError(err);
    }
  };

  const handleClear = () => setFileEntry(null);

  // Staged file — preview + upload button
  if (fileEntry) {
    const isUploading = fileEntry.status === "uploading";
    const isSuccess = fileEntry.status === "success";

    return (
      <Box sx={{ p: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1.5,
            borderRadius: 1,
            border: 1,
            borderColor: isSuccess ? "success.main" : fileEntry.status === "error" ? "error.main" : "divider",
            bgcolor: alpha(
              isSuccess
                ? theme.palette.success.main
                : fileEntry.status === "error"
                  ? theme.palette.error.main
                  : theme.palette.action.hover,
              0.06
            ),
          }}
        >
          <InsertDriveFileOutlined sx={{ color: "text.secondary", fontSize: 20 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap title={fileEntry.file.name}>
              {fileEntry.file.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(fileEntry.file.size)}
            </Typography>
            {isUploading && <LinearProgress variant="indeterminate" sx={{ mt: 0.5, borderRadius: 1 }} />}
            {fileEntry.error && (
              <Typography variant="caption" color="error.main">
                {fileEntry.error}
              </Typography>
            )}
          </Box>
          {!isUploading && (
            <IconButton size="small" onClick={handleClear} title="Remove">
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {fileEntry.status === "pending" || fileEntry.status === "error" ? (
          <Button
            variant="contained"
            size="small"
            onClick={handleUpload}
            startIcon={<CloudUploadOutlined />}
            fullWidth
            sx={{ mt: 1 }}
          >
            Upload
          </Button>
        ) : null}
      </Box>
    );
  }

  // Drop zone — no file staged
  return (
    <Box sx={{ p: fullHeight ? 0 : 1.5 }}>
      {validationError && (
        <Alert severity="error" onClose={() => setValidationError(null)} sx={{ mb: 1.5 }}>
          {validationError}
        </Alert>
      )}
      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          p: 2,
          borderRadius: fullHeight ? 0 : 1,
          border: "2px dashed",
          borderColor: isDragging ? "primary.main" : "divider",
          bgcolor: isDragging
            ? alpha(theme.palette.primary.main, 0.06)
            : alpha(isDarkMode ? theme.palette.grey[800] : theme.palette.grey[50], 0.5),
          cursor: "pointer",
          transition: "border-color 0.15s, background-color 0.15s",
          "&:hover": {
            borderColor: "primary.main",
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          },
          ...(fullHeight && { flex: 1, height: "100%" }),
        }}
      >
        <CloudUploadOutlined sx={{ fontSize: 40, color: "text.secondary" }} />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Drop a reference document here or click to browse
        </Typography>
        <Typography variant="caption" color="text.disabled" textAlign="center">
          PDF, Word, Excel, images, text, or email — max {FILE_CONSTANTS.MAX_FILE_SIZE_MB} MB
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS.join(",")}
          onChange={handleInputChange}
          hidden
        />
      </Box>
    </Box>
  );
};
